// Required-field rules for Aptly web forms.
//
// MIRRORS core's `imports/fields/required.js` (the Meteor app). The two copies must change
// together: the server re-checks every web form submission with core's copy, so a rule that
// differs here lets the client accept something the server then rejects (or the reverse). Both are
// held to the same test vectors: core's `tests/imports/fields/required.test.js` and this repo's
// `src/utils/required-fields.test.js`.
//
// A field is required when it says so — board fields use `optional: false`, web form fields use
// `required: true` — unless its type can never be meaningfully "missing" (a checkbox is always
// either checked or not) or the user cannot edit it.

// Types that are never required-blocking: booleans, checklists, and anything computed or
// display-only.
const NEVER_REQUIRED_TYPES = new Set([
  'boolean',
  'boolean-checkbox',
  'showingEnabled',
  'applyActionEnabled',
  'qualified',
  'checkboxlist',
  'checkbox-list',
  'calculation',
  'ai-computed',
  'cardId',
  'contactType',
  'daysOpened',
  'timeSince',
  'listings-report',
  'stringList',
  'screeningProvider'
]);

const NUMBER_TYPES = new Set(['number', 'percent', 'multiplier']);
const DATE_TYPES = new Set(['date', 'datetime-local']);
const ADDRESS_TYPES = new Set(['address', 'google-places-input']);
const PHONE_TYPES = new Set(['tel', 'phone']);
const PERSON_TYPES = new Set(['person']);

const isBlankString = (value) => typeof value === 'string' && value.trim() === '';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);

// '' and whitespace are empty; '0', 0 and '-0' are real numbers. NaN (what a number input emits
// for a lone '-') counts as empty.
const isEmptyNumber = (value) => {
  if (typeof value === 'number') return Number.isNaN(value);
  if (typeof value === 'string') {
    const cleaned = value.replace(/[\s,%]/g, '');
    return cleaned === '' || Number.isNaN(Number(cleaned));
  }
  return true;
};

// Cards store money as `{ amount, currency }` (amount in cents, a js-money instance on the
// client); web forms store the formatted string ("$1,234.00"). $0 is a real amount in both.
const isEmptyMoney = (value) => {
  if (value && typeof value === 'object') return isEmptyNumber(value.amount);
  if (typeof value === 'string') return isEmptyNumber(value.replace(/[^\d.-]/g, ''));
  return isEmptyNumber(value);
};

const isEmptyDate = (value) => {
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (typeof value === 'string') return value.trim() === '' || Number.isNaN(Date.parse(value));
  if (typeof value === 'number') return Number.isNaN(value);
  if (value && typeof value.toDate === 'function') return isEmptyDate(value.toDate()); // moment
  return true;
};

// A masked phone input can hold "(___) ___-____" — no digits means nothing was entered.
const isEmptyPhone = (value) => {
  if (typeof value === 'string') return !/\d/.test(value);
  if (typeof value === 'number') return Number.isNaN(value);
  if (isPlainObject(value)) return isEmptyPhone(value.number);
  return true;
};

const isEmptyAddress = (value) => {
  if (typeof value === 'string') return isBlankString(value);
  if (isPlainObject(value)) return isEmptyGeneric(value.formattedAddress);
  return true;
};

// Cards hold `{ _id, name }`, web forms a contact `{ firstName, lastName, email, phone }`.
const PERSON_KEYS = ['_id', 'personId', 'name', 'firstName', 'lastName', 'email'];
const isEmptyPerson = (value) => {
  if (typeof value === 'string') return isBlankString(value);
  // Some cards hold the person as a one-item array.
  if (Array.isArray(value)) return value.every(isEmptyPerson);
  if (isPlainObject(value)) return PERSON_KEYS.every((key) => isEmptyGeneric(value[key]));
  return true;
};

// Rich text editors leave markup behind when cleared (`<p><br></p>`, `&nbsp;`), so only the
// visible text counts.
const isEmptyRichText = (value) => {
  if (typeof value !== 'string') return isEmptyGeneric(value);
  const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;/gi, ' ');
  return text.trim() === '' && !/<(img|iframe|video|embed|object)\b/i.test(value);
};

const isEmptyGeneric = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return isBlankString(value);
  if (typeof value === 'number') return Number.isNaN(value);
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
};

/**
 * Whether a field's value counts as "not filled in".
 * 0, '0', $0 and `false` are real values; whitespace, [], {} and NaN are not.
 */
export const isValueEmpty = (value, type) => {
  if (value === null || value === undefined) return true;
  if (NUMBER_TYPES.has(type)) return isEmptyNumber(value);
  if (type === 'money') return isEmptyMoney(value);
  if (DATE_TYPES.has(type)) return isEmptyDate(value);
  if (PHONE_TYPES.has(type)) return isEmptyPhone(value);
  if (ADDRESS_TYPES.has(type)) return isEmptyAddress(value);
  if (PERSON_TYPES.has(type)) return isEmptyPerson(value);
  if (type === 'rich-text') return isEmptyRichText(value);
  return isEmptyGeneric(value);
};

/**
 * Whether a field must be filled in before saving. Board fields mark it with `optional: false`,
 * web form fields with `required: true`. Never true for booleans, checklists, computed or
 * display-only types, or fields the user cannot edit.
 */
export const isFieldRequired = (field) => {
  if (!field) return false;
  if (NEVER_REQUIRED_TYPES.has(field.type)) return false;
  if (field.metricField || field.readOnly || field.mirrorTarget) return false;
  if (field.editable === false) return false;
  return field.optional === false || field.required === true;
};

/**
 * Reads a field's value by uuid. Some uuids are dotted (`aptlyListings.showingProvider`), and
 * Formik stores those nested, so the nested value wins over a flat key of the same name.
 */
export const getFieldValue = (values, uuid) => {
  if (!values || !uuid) return undefined;
  if (uuid.includes('.')) {
    const nested = uuid
      .split('.')
      .reduce((obj, key) => (obj && typeof obj === 'object' ? obj[key] : undefined), values);
    if (nested !== undefined) return nested;
  }
  return values[uuid];
};

export const requiredMessage = (field) => `${field?.name || 'This field'} is required.`;

/**
 * The required fields in `fields` that have no value in `values`, in the order given.
 * Pass only the fields the user can actually see — a hidden field cannot be filled in.
 */
export const getMissingRequiredFields = ({
  fields = [],
  values = {},
  isRequired = isFieldRequired
}) =>
  fields
    .filter((field) => field?.uuid && isRequired(field))
    .filter((field) => isValueEmpty(getFieldValue(values, field.uuid), field.type))
    .map((field) => ({ uuid: field.uuid, name: field.name, message: requiredMessage(field) }));
